import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { useRealtime } from "../hooks/RealtimeContext";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { 
  Button, 
  Dialog, 
  Grid, 
  Box, 
  Typography, 
  Alert,
  AlertTitle,
  TextField,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Chip,
  Divider,
  Collapse,
  CircularProgress,
  Badge,
  InputAdornment,
  Tabs,
  Tab,
  Tooltip,
  useMediaQuery
} from "@mui/material";
import {
  fetchGetMesaAbierta,
  fetchGetMesasAbiertas,
  fetchGuardarComandaMesa,
  fetchGetMapaMesas,
  fetchGetLocalesComandaMesa,
  mapMesaDetalleToLineaVenta,
  MesaAbiertaResponse,
  MesaComandaResponse,
  MapaMesasSectorResponse,
  MapaMesaResponse
} from "../services/mesaService";
import { LocalModel } from "../models/LocalModel";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from "@mui/icons-material/Delete";
import TableBarIcon from '@mui/icons-material/TableBar';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ClearIcon from '@mui/icons-material/Clear';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import { CurrencyFormatter } from "../utils/CurrencyFormatter";
import LocalSelector from "./components/LocalSelector";
import { parseDecimalValue } from "../utils/NumberParser";
import { isAbortError } from "../services/fetchService";
import { useProductosVenta } from "../hooks/ProductosVentaContext";
import { CatalogoVentaActualizadoDialog } from "../components/CatalogoVentaActualizadoDialog";
import { fetchProductoComandaMesaImagen } from "../services/productoService";
import { fetchAppConfig } from "../services/configService";

const imagenProductoCache = new Map<number, string | undefined>();

const obtenerLimitesMapa = (mesas: MapaMesaResponse[]) => {
  if (mesas.length === 0) {
    return { minX: 0, minY: 0, ancho: 1000, alto: 600 };
  }

  const minX = Math.min(...mesas.map((mesa) => mesa.x));
  const minY = Math.min(...mesas.map((mesa) => mesa.y));
  const maxX = Math.max(...mesas.map((mesa) => mesa.x + mesa.ancho));
  const maxY = Math.max(...mesas.map((mesa) => mesa.y + mesa.alto));

  return {
    minX,
    minY,
    ancho: Math.max(maxX - minX, 1),
    alto: Math.max(maxY - minY, 1)
  };
};

const ImagenProductoComanda = ({ producto, user }: { producto: Producto; user: any }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const idProducto = Number(producto.IDPRODUCTO ?? 0);
  const [visible, setVisible] = useState(false);
  const [imagen, setImagen] = useState<string | undefined>(() => imagenProductoCache.get(idProducto));

  useEffect(() => {
    const elemento = ref.current;
    if (!elemento) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "0px" });
    observer.observe(elemento);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !user || idProducto <= 0 || imagenProductoCache.has(idProducto)) {
      setImagen(imagenProductoCache.get(idProducto));
      return;
    }
    const controller = new AbortController();
    fetchProductoComandaMesaImagen(user, idProducto, controller.signal)
      .then((url) => {
        imagenProductoCache.set(idProducto, url);
        setImagen(url);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        imagenProductoCache.set(idProducto, undefined);
      });
    return () => controller.abort();
  }, [idProducto, user, visible]);

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        height: 130,
        minHeight: 130,
        flex: "0 0 130px",
        overflow: "hidden",
        bgcolor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      {imagen ? (
        <CardMedia
          component="img"
          image={imagen}
          alt={producto.PRODUCTO}
          sx={{
            display: "block",
            width: "100%",
            maxWidth: "100%",
            height: 130,
            minHeight: 130,
            flexShrink: 0,
            objectFit: "cover"
          }}
        />
      ) : (
        <RestaurantIcon sx={{ fontSize: 44, color: "text.disabled" }} />
      )}
    </Box>
  );
};

const ComandaMesa = () => {
  const isMobileViewport = useMediaQuery("(max-width: 599px)");
  const isLandscapeTablet = useMediaQuery("(orientation: landscape) and (min-width: 768px) and (pointer: coarse)");
  const fullScreenProductDialog = isMobileViewport || isLandscapeTablet;
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const { subscribe } = useRealtime();
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [localesComanda, setLocalesComanda] = useState<LocalModel[] | null>(null);
  const [selectDialogOpen, setSelectDialogOpen] = useState<boolean>(false);
  const [busquedaProducto, setBusquedaProducto] = useState("");
  const [rubroSeleccionado, setRubroSeleccionado] = useState("");
  const [subrubroSeleccionado, setSubrubroSeleccionado] = useState("");
  const [numeroMesa, setNumeroMesa] = useState<string>("");
  const [nombreComanda, setNombreComanda] = useState<string>("");
  const numeroMesaRef = useRef<HTMLDivElement | null>(null);
  const [lineaVenta, setLineaVenta] = useState<LineaVenta[]>([]);
  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error") => {
    showNotification(message, severity);
  }, [showNotification]);
  const { productos, loading: loadingProductos, revision } = useProductosVenta(idLocal);
  const [checkingMesa, setCheckingMesa] = useState(false);
  const [loadingMesasAbiertas, setLoadingMesasAbiertas] = useState(false);
  const [comandasAbiertas, setComandasAbiertas] = useState<MesaComandaResponse[]>([]);
  const [refreshMesasAbiertas, setRefreshMesasAbiertas] = useState(0);
  const [mapaSectores, setMapaSectores] = useState<MapaMesasSectorResponse[]>([]);
  const [sectorMapaSeleccionado, setSectorMapaSeleccionado] = useState(0);
  const [loadingMapa, setLoadingMapa] = useState(false);
  const [mapaExpandido, setMapaExpandido] = useState(false);
  const [mesaAbiertaDetalle, setMesaAbiertaDetalle] = useState<MesaAbiertaResponse | undefined>(undefined);
  const [mesaAbiertaCargada, setMesaAbiertaCargada] = useState(false);
  const [idsComandasOriginales, setIdsComandasOriginales] = useState<number[]>([]);
  const [idMesaSeleccionada, setIdMesaSeleccionada] = useState<number | null>(null);
  const [expandedNotes, setExpandedNotes] = useState<{ [key: number]: boolean }>({});
  const [editingNotes, setEditingNotes] = useState<{ [key: number]: string }>({});
  const [editingQuantities, setEditingQuantities] = useState<{ [key: number]: string }>({});
  const [enviandoComanda, setEnviandoComanda] = useState(false);
  const enviandoComandaRef = useRef(false);
  const mesaBusquedaActualRef = useRef({ idLocal: 0, identificador: "" });
  const pv = useMemo(() => Number(localesComanda?.find((local) => String(local.idLocal) === String(idLocal))?.pvComandaMesaWeb ?? 0), [idLocal, localesComanda]);
  const [decimalesEnCantidad, setDecimalesEnCantidad] = useState(2);
  const pasoCantidad = 1;

  const redondearCantidad = useCallback((cantidad: number) => {
    const factor = Math.pow(10, decimalesEnCantidad);
    return Math.round(cantidad * factor) / factor;
  }, [decimalesEnCantidad]);

  const formatearCantidad = useCallback((cantidad: number) => cantidad.toLocaleString("es-AR", {
    minimumFractionDigits: decimalesEnCantidad,
    maximumFractionDigits: decimalesEnCantidad
  }), [decimalesEnCantidad]);

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const closeProductDialog = () => {
    blurActiveElement();
    setSelectDialogOpen(false);
    setBusquedaProducto("");
    setRubroSeleccionado("");
    setSubrubroSeleccionado("");
  };

  const handleOpenProductDialog = () => {
    if (!idLocal) {
      showMessage("Debe seleccionar un local primero", 'error');
      return;
    }

    if (!numeroMesa.trim() && !nombreComanda.trim()) {
      showMessage("Debe ingresar un número de mesa o un nombre", 'error');
      return;
    }

    if (productos.length === 0) {
      showMessage("Cargando productos...", 'info');
      return;
    }

    blurActiveElement();
    setSelectDialogOpen(true);
  };

  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);

  useEffect(() => {
    if (!userLoggedIn) {
      setLocalesComanda(null);
      return;
    }
    const controller = new AbortController();
    fetchGetLocalesComandaMesa(userLoggedIn, controller.signal)
      .then(setLocalesComanda)
      .catch((error) => {
        if (isAbortError(error)) return;
        console.error(error);
        setLocalesComanda([]);
        showMessage("No se pudieron cargar los locales habilitados para Comanda Mesa", "error");
      });
    return () => controller.abort();
  }, [showMessage, userLoggedIn]);

  useEffect(() => {
    if (!userLoggedIn || !idLocal || pv <= 0) {
      setDecimalesEnCantidad(2);
      return;
    }

    const controller = new AbortController();
    fetchAppConfig(userLoggedIn, Number(idLocal), pv)
      .then((config) => setDecimalesEnCantidad(Math.max(0, Math.min(6, config?.decimalesEnCantidad ?? 2))))
      .catch((error) => {
        if (!isAbortError(error)) setDecimalesEnCantidad(2);
      });
    return () => controller.abort();
  }, [idLocal, pv, userLoggedIn]);

  const mesasAbiertas = useMemo(() => {
    const grupos = new Map<number, {
      idMesa: number;
      nombre: string;
      cantidadComandas: number;
      total: number;
      fecha: string;
    }>();

    comandasAbiertas.forEach((comanda) => {
      const existente = grupos.get(comanda.idMesa);
      if (existente) {
        existente.cantidadComandas += 1;
        existente.total += comanda.total;
        if (!existente.nombre && comanda.nombre) existente.nombre = comanda.nombre;
        if (comanda.fecha > existente.fecha) existente.fecha = comanda.fecha;
      } else {
        grupos.set(comanda.idMesa, {
          idMesa: comanda.idMesa,
          nombre: comanda.nombre,
          cantidadComandas: 1,
          total: comanda.total,
          fecha: comanda.fecha
        });
      }
    });

    return Array.from(grupos.values()).sort((a, b) => b.fecha.localeCompare(a.fecha));
  }, [comandasAbiertas]);

  useEffect(() => {
    const unsubscribeMesas = subscribe("MesasAbiertasActualizadas", (evento: { idLocal?: number; pv?: number }) => {
      if (Number(evento?.idLocal) !== Number(idLocal) || Number(evento?.pv) !== pv) return;
      setRefreshMesasAbiertas((actual) => actual + 1);
    });
    const unsubscribeReconectar = subscribe("RealtimeReconnected", () => {
      if (idLocal && pv > 0) setRefreshMesasAbiertas((actual) => actual + 1);
    });

    return () => {
      unsubscribeMesas();
      unsubscribeReconectar();
    };
  }, [idLocal, pv, subscribe]);

  const normalizeProducto = (producto: any): Producto => ({
    IDPRODUCTO: Number(producto.IDPRODUCTO ?? producto.idProducto ?? producto.IdProducto ?? 0),
    CODIGO: String(producto.CODIGO ?? producto.codigo ?? producto.Codigo ?? ""),
    PRODUCTO: String(producto.PRODUCTO ?? producto.producto ?? producto.Producto ?? ""),
    CONTADO: parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0),
    IDRUBRO: Number(producto.IDRUBRO ?? producto.idRubro ?? producto.IdRubro ?? 0),
    RUBRO: String(producto.RUBRO ?? producto.rubro ?? producto.Rubro ?? ""),
    IDSUBRUBRO: Number(producto.IDSUBRUBRO ?? producto.idSubrubro ?? producto.IdSubrubro ?? 0),
    SUBRUBRO: String(producto.SUBRUBRO ?? producto.subrubro ?? producto.Subrubro ?? "")
  });

  const getUnitario = (linea: LineaVenta): number => {
    const producto = linea.producto as any;
    return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0);
  };

  useEffect(() => {
    if (!userLoggedIn || !idLocal || pv <= 0) {
      setComandasAbiertas([]);
      return;
    }

    const controller = new AbortController();
    setLoadingMesasAbiertas(true);
    fetchGetMesasAbiertas(userLoggedIn, Number(idLocal), pv, controller.signal)
      .then(setComandasAbiertas)
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        showMessage("No se pudieron cargar las mesas abiertas", "error");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMesasAbiertas(false);
      });

    return () => controller.abort();
  }, [idLocal, pv, refreshMesasAbiertas, showMessage, userLoggedIn]);

  useEffect(() => {
    if (!userLoggedIn || !idLocal || pv <= 0) {
      setMapaSectores([]);
      return;
    }
    const controller = new AbortController();
    setLoadingMapa(true);
    fetchGetMapaMesas(userLoggedIn, Number(idLocal), controller.signal)
      .then((mapa) => {
        setMapaSectores(mapa.sectores);
        setSectorMapaSeleccionado(0);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        setMapaSectores([]);
        showMessage("No se pudo cargar el mapa de mesas", "warning");
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingMapa(false); });
    return () => controller.abort();
  }, [idLocal, pv, showMessage, userLoggedIn]);

  useEffect(() => {
    mesaBusquedaActualRef.current = {
      idLocal: Number(idLocal ?? 0),
      identificador: numeroMesa.trim()
    };

    if (idMesaSeleccionada != null && idMesaSeleccionada < 0) {
      return;
    }

    if (!userLoggedIn || !idLocal || pv <= 0 || !numeroMesa.trim()) {
      setMesaAbiertaDetalle(undefined);
      setMesaAbiertaCargada(false);
      setIdsComandasOriginales([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => {
      setCheckingMesa(true);
      const idLocalBuscado = Number(idLocal);
      const numeroMesaBuscado = numeroMesa.trim();
      fetchGetMesaAbierta(userLoggedIn, idLocalBuscado, pv, numeroMesaBuscado, controller.signal)
        .then((mesa: MesaAbiertaResponse) => {
          if (
            mesaBusquedaActualRef.current.idLocal !== idLocalBuscado ||
            mesaBusquedaActualRef.current.identificador !== numeroMesaBuscado
          ) {
            return;
          }

          const detalleMesa = mesa.detalle ?? [];
          const mesaTieneDetalle = detalleMesa.length > 0;
          const mesaAbierta = Boolean(mesa.abierta || mesaTieneDetalle);
          setMesaAbiertaDetalle(mesaAbierta ? {
            abierta: true,
            comanda: mesa.comanda,
            detalle: detalleMesa
          } : undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
        })
        .catch(err => {
          if (isAbortError(err)) return;
          console.log(err);
          setMesaAbiertaDetalle(undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setCheckingMesa(false);
        });
    }, 600);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [userLoggedIn, idLocal, pv, numeroMesa, idMesaSeleccionada]);

  const handleProductClick = (producto: Producto) => {
    try {
      const row = normalizeProducto(producto);
      setLineaVenta((lineas) => {
        const index = lineas.findIndex((linea) =>
          !linea.idComanda && !linea.notas && Number((linea.producto as any).IDPRODUCTO) === row.IDPRODUCTO);
        if (index < 0) return [...lineas, { producto: row, cantidad: 1, notas: "" }];
        return lineas.map((linea, current) => current === index ? { ...linea, cantidad: redondearCantidad(linea.cantidad + 1) } : linea);
      });
    } catch (error) {
      console.error("Error adding product:", error);
    }
  };

  const rubrosDisponibles = useMemo(() => {
    const rubros = new Map<string, string>();
    productos.forEach((producto) => {
      const id = String(producto.IDRUBRO ?? 0);
      const nombre = String(producto.RUBRO ?? "").trim();
      if (nombre) rubros.set(id, nombre);
    });
    return Array.from(rubros, ([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const subrubrosDisponibles = useMemo(() => {
    if (!rubroSeleccionado) return [];

    const subrubros = new Map<string, string>();
    productos.forEach((producto) => {
      if (String(producto.IDRUBRO ?? 0) !== rubroSeleccionado) return;
      const id = String(producto.IDSUBRUBRO ?? 0);
      const nombre = String(producto.SUBRUBRO ?? "").trim();
      if (nombre) subrubros.set(id, nombre);
    });
    return Array.from(subrubros, ([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos, rubroSeleccionado]);

  const productosFiltrados = useMemo(() => {
    const filtro = busquedaProducto.trim().toLocaleLowerCase();
    return productos.filter((producto) => {
      const coincideRubro = !rubroSeleccionado || String(producto.IDRUBRO ?? 0) === rubroSeleccionado;
      const coincideSubrubro = !subrubroSeleccionado || String(producto.IDSUBRUBRO ?? 0) === subrubroSeleccionado;
      const coincideBusqueda = !filtro
        || String(producto.PRODUCTO ?? "").toLocaleLowerCase().includes(filtro)
        || String(producto.CODIGO ?? "").toLocaleLowerCase().includes(filtro);
      return coincideRubro && coincideSubrubro && coincideBusqueda;
    });
  }, [busquedaProducto, productos, rubroSeleccionado, subrubroSeleccionado]);

  const cantidadProductoAgregada = useCallback((producto: Producto) =>
    lineaVenta
      .filter((linea) => Number((linea.producto as any).IDPRODUCTO) === Number(producto.IDPRODUCTO))
      .reduce((cantidad, linea) => cantidad + linea.cantidad, 0), [lineaVenta]);

  const getLineasMesaAbierta = (): LineaVenta[] => {
    return mesaAbiertaDetalle?.detalle?.map(mapMesaDetalleToLineaVenta) ?? [];
  };

  const calcularTotalLineas = (lineas: LineaVenta[]): number => {
    return lineas.reduce(
      (total, linea) => total + (getUnitario(linea) * linea.cantidad),
      0
    );
  };

  const getLineasMesaAbiertaNoCargadas = (): LineaVenta[] => {
    return mesaAbiertaCargada ? [] : getLineasMesaAbierta();
  };

  const traerDetalleMesaAbierta = () => {
    const lineasMesa = getLineasMesaAbierta();

    if (lineasMesa.length === 0) {
      setLineaVenta([]);
      setMesaAbiertaCargada(true);
      setIdsComandasOriginales([]);
      showMessage("Mesa abierta sin detalle. Puede agregar productos.", 'info');
      return;
    }

    setLineaVenta(lineasMesa);
    setMesaAbiertaCargada(true);
    setIdsComandasOriginales(Array.from(new Set(lineasMesa.map((linea) => linea.idComanda).filter(Boolean) as number[])));
    showMessage("Detalle de mesa cargado", 'success');
  };

  const handleIncrementQuantity = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta[rowIndex].cantidad = redondearCantidad(updatedLineaVenta[rowIndex].cantidad + 1);
    setLineaVenta(updatedLineaVenta);
  };

  const handleDecrementQuantity = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    if (updatedLineaVenta[rowIndex].cantidad > 1) {
      updatedLineaVenta[rowIndex].cantidad = redondearCantidad(updatedLineaVenta[rowIndex].cantidad - 1);
      setLineaVenta(updatedLineaVenta);
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta.splice(rowIndex, 1);
    setLineaVenta(updatedLineaVenta);
    // Clean up expanded/editing state for this row
    const newExpandedNotes = { ...expandedNotes };
    const newEditingNotes = { ...editingNotes };
    delete newExpandedNotes[rowIndex];
    delete newEditingNotes[rowIndex];
    setExpandedNotes(newExpandedNotes);
    setEditingNotes(newEditingNotes);
    setEditingQuantities((current) => {
      const next: { [key: number]: string } = {};
      Object.keys(current).forEach((key) => {
        const oldIndex = Number(key);
        if (oldIndex < rowIndex) next[oldIndex] = current[oldIndex];
        if (oldIndex > rowIndex) next[oldIndex - 1] = current[oldIndex];
      });
      return next;
    });
  };

  const toggleNotesExpand = (rowIndex: number) => {
    setExpandedNotes(prev => ({
      ...prev,
      [rowIndex]: !prev[rowIndex]
    }));
    // Initialize editing state if needed
    if (!editingNotes[rowIndex]) {
      setEditingNotes(prev => ({
        ...prev,
        [rowIndex]: lineaVenta[rowIndex].notas || ""
      }));
    }
  };

  const handleNotesChange = (rowIndex: number, value: string) => {
    setEditingNotes(prev => ({
      ...prev,
      [rowIndex]: value
    }));
  };

  const saveNotes = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta[rowIndex].notas = editingNotes[rowIndex];
    setLineaVenta(updatedLineaVenta);
    setExpandedNotes(prev => ({
      ...prev,
      [rowIndex]: false
    }));
  };

  const calcularTotal = (): number => {
    return calcularTotalLineas(lineaVenta);
  };

  const calcularTotalMesa = (): number => {
    return calcularTotalLineas(lineaVenta) + calcularTotalLineas(getLineasMesaAbiertaNoCargadas());
  };

  const totalAnteriorMesaRaw = (mesaAbiertaDetalle?.comanda as any)?.total
    ?? (mesaAbiertaDetalle?.comanda as any)?.Total;
  const mostrarDesgloseTotalMesa = mesaAbiertaCargada
    && Boolean(mesaAbiertaDetalle?.detalle?.length)
    && totalAnteriorMesaRaw != null;
  const totalAnteriorMesa = parseDecimalValue(totalAnteriorMesaRaw ?? 0);
  const totalProductosAgregados = calcularTotalLineas(lineaVenta.filter((linea) => !linea.idComanda));

  const guardarComanda = () => {
    if (enviandoComandaRef.current) return;

    if (idLocal && pv <= 0) {
      showMessage("El local seleccionado no tiene un PV asociado válido para Comanda Mesa Web", 'error');
      return;
    }

    if (!userLoggedIn || !idLocal || (!numeroMesa.trim() && !nombreComanda.trim())) {
      showMessage("Complete todos los campos requeridos", 'error');
      return;
    }

    if (lineaVenta.length === 0) {
      showMessage("No hay productos en la comanda", 'error');
      return;
    }

    enviandoComandaRef.current = true;
    setEnviandoComanda(true);
    fetchGuardarComandaMesa(userLoggedIn!, Number(idLocal), numeroMesa.trim(), lineaVenta, {
      pv,
      eliminarAnterior: true,
      idComandasOriginales: idsComandasOriginales,
      nombre: nombreComanda.trim(),
      idMesa: idMesaSeleccionada != null && idMesaSeleccionada < 0
        ? idMesaSeleccionada
        : Number(numeroMesa.trim() || 0)
    })
      .then((res: any) => {
        const success = Boolean(res?.success ?? true);
        showMessage(success ? "Comanda enviada correctamente" : res?.message ?? "No se pudo guardar la comanda", success ? 'success' : 'error');
        
        if (success) {
          // Reset form
          setLineaVenta([]);
          setNumeroMesa("");
          setNombreComanda("");
          setIdMesaSeleccionada(null);
          setMesaAbiertaDetalle(undefined);
          setMesaAbiertaCargada(false);
          setIdsComandasOriginales([]);
          setRefreshMesasAbiertas((value) => value + 1);
          window.requestAnimationFrame(() => {
            numeroMesaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      })
      .catch(err => {
        showMessage(err instanceof Error ? err.message : "Error al guardar la comanda", 'error');
        console.log(err);
      })
      .finally(() => {
        enviandoComandaRef.current = false;
        setEnviandoComanda(false);
      });
  };

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
    setIdMesaSeleccionada(null);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
    setMapaSectores([]);
    setSectorMapaSeleccionado(0);
    setMapaExpandido(false);
  };

  const handleNumeroMesaChange = (value: string) => {
    setNumeroMesa(value);
    setIdMesaSeleccionada(null);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
  };

  const handleNombreComandaChange = (value: string) => {
    setNombreComanda(value);
    setIdMesaSeleccionada(null);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
  };

  const cargarDetalleComandaNombrada = (mesa: MesaAbiertaResponse) => {
    const detalleMesa = mesa.detalle ?? [];
    const lineasMesa = detalleMesa.map(mapMesaDetalleToLineaVenta);
    setMesaAbiertaDetalle({
      abierta: true,
      comanda: mesa.comanda,
      detalle: detalleMesa
    });
    setLineaVenta(lineasMesa);
    setMesaAbiertaCargada(true);
    setIdsComandasOriginales(Array.from(new Set(lineasMesa.map((linea) => linea.idComanda).filter(Boolean) as number[])));
  };

  const seleccionarMesaAbierta = (mesa: typeof mesasAbiertas[number]) => {
    setExpandedNotes({});
    setEditingNotes({});
    if (mesa.idMesa < 0) {
      if (!userLoggedIn || !idLocal) {
        showMessage("Debe seleccionar un local primero", "error");
        return;
      }
      setIdMesaSeleccionada(mesa.idMesa);
      setNumeroMesa("");
      setNombreComanda(mesa.nombre);
      setCheckingMesa(true);
      fetchGetMesaAbierta(userLoggedIn, Number(idLocal), pv, String(mesa.idMesa))
        .then((abierta) => {
          if (!abierta.abierta && (abierta.detalle?.length ?? 0) === 0) {
            showMessage("Esa comanda ya no está abierta o fue facturada", "warning");
            setIdMesaSeleccionada(null);
            setMesaAbiertaDetalle(undefined);
            setMesaAbiertaCargada(false);
            setIdsComandasOriginales([]);
            setLineaVenta([]);
            return;
          }
          cargarDetalleComandaNombrada(abierta);
        })
        .catch((err) => {
          console.log(err);
          showMessage("No se pudo cargar la comanda seleccionada", "error");
        })
        .finally(() => setCheckingMesa(false));
      return;
    }

    setIdMesaSeleccionada(mesa.idMesa);
    setNumeroMesa(mesa.idMesa.toString());
    setNombreComanda(mesa.nombre);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
  };

  const beginQuantityEdit = (rowIndex: number) => {
    setEditingQuantities((current) => ({
      ...current,
      [rowIndex]: String(lineaVenta[rowIndex].cantidad).replace(".", ",")
    }));
  };

  const changeQuantityText = (rowIndex: number, value: string) => {
    setEditingQuantities((current) => ({ ...current, [rowIndex]: value }));
  };

  const finishQuantityEdit = (rowIndex: number) => {
    const rawValue = editingQuantities[rowIndex]?.trim() ?? "";
    const value = parseDecimalValue(rawValue);
    if (value <= 0 || !Number.isFinite(value)) {
      setEditingQuantities((current) => ({ ...current, [rowIndex]: formatearCantidad(lineaVenta[rowIndex].cantidad) }));
      return;
    }

    const updated = [...lineaVenta];
    updated[rowIndex].cantidad = redondearCantidad(value);
    setLineaVenta(updated);
    setEditingQuantities((current) => {
      const next = { ...current };
      delete next[rowIndex];
      return next;
    });
  };

  const seleccionarMesaMapa = (mesa: MapaMesaResponse) => {
    const abierta = mesasAbiertas.find((item) => item.idMesa === mesa.numero);
    if (abierta) {
      seleccionarMesaAbierta(abierta);
      showMessage(`Mesa ${mesa.numero} ocupada seleccionada`, "info");
      return;
    }
    handleNumeroMesaChange(String(mesa.numero));
    setNombreComanda("");
    showMessage(`Mesa ${mesa.numero} libre seleccionada`, "success");
  };

  const haySeleccionAbierta = idMesaSeleccionada != null || mesaAbiertaCargada || Boolean(mesaAbiertaDetalle);
  const limitesMapaSectores = useMemo(
    () => new Map(mapaSectores.map((sector) => [sector.idSector, obtenerLimitesMapa(sector.mesas)] as const)),
    [mapaSectores]
  );

  const limpiarSeleccion = () => {
    setNumeroMesa("");
    setNombreComanda("");
    setIdMesaSeleccionada(null);
    setMesaAbiertaDetalle(undefined);
    setMesaAbiertaCargada(false);
    setIdsComandasOriginales([]);
    setLineaVenta([]);
    setExpandedNotes({});
    setEditingNotes({});
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <CatalogoVentaActualizadoDialog productos={productos} revision={revision} lineas={lineaVenta} setLineas={setLineaVenta} />
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}
      >
        <RestaurantIcon fontSize="large" color="primary" />
        Comanda de Mesa
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Los botones + y − ajustan la cantidad de a una unidad. También puede ingresar cantidades decimales manualmente.
      </Typography>
      {/* <Alert severity="info" sx={{ mb: 2 }}>
        Las cantidades aceptan hasta {decimalesEnCantidad} decimal{decimalesEnCantidad === 1 ? "" : "es"} según la parametrización del local y punto de venta.
      </Alert> */}

      {/* Local and Table Selection */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 0.25, sm: 0.5 } }}>
        {localesComanda && localesComanda.length > 0 && (
          <Grid item xs={12} md={localesComanda.length > 1 ? 4 : 0} sx={{ display: localesComanda.length > 1 ? "block" : "none" }}>
            <LocalSelector
              onLocalChange={handleLocalChange}
              localesPermitidos={localesComanda}
              ocultarSiHayUno
            />
          </Grid>
        )}
        <Grid item ref={numeroMesaRef} xs={12} md={localesComanda?.length === 1 ? 6 : 4} sx={{ scrollMarginTop: 16 }}>
          <TextField
            label="Número de mesa (opcional)"
            variant="outlined"
            fullWidth
            value={numeroMesa}
            onChange={(e) => handleNumeroMesaChange(e.target.value)}
            helperText={checkingMesa ? "Buscando mesa abierta..." : " "}
            InputProps={{
              startAdornment: <TableBarIcon sx={{ mr: 1, color: 'action.active' }} />,
            }}
          />
        </Grid>
      </Grid>
      {idLocal && (loadingMapa || mapaSectores.length > 0) && (
        <Box sx={{ mb: { xs: 1, sm: 1.5 }, border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden", mt: -0.5 }}>
          <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Button onClick={() => setMapaExpandido((value) => !value)} startIcon={<TableBarIcon />} sx={{ textTransform: "none", fontWeight: 700 }} aria-expanded={mapaExpandido}>
              {mapaExpandido ? "Ocultar mapa de mesas" : "Mostrar mapa de mesas"}
            </Button>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Chip size="small" label="Libre" sx={{ bgcolor: "success.light", color: "success.contrastText" }} />
              <Chip size="small" label="Ocupada" sx={{ bgcolor: "error.light", color: "error.contrastText" }} />
            </Box>
          </Box>
          <Collapse in={mapaExpandido} timeout="auto" unmountOnExit>
          {loadingMapa ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}><CircularProgress size={20} /><Typography variant="body2">Cargando mapa...</Typography></Box>
          ) : (
            <>
              <Tabs value={Math.min(sectorMapaSeleccionado, Math.max(0, mapaSectores.length - 1))} onChange={(_, value) => setSectorMapaSeleccionado(value)} variant="scrollable" scrollButtons="auto" aria-label="Sectores del mapa de mesas">
                {mapaSectores.map((sector) => <Tab key={sector.idSector} label={sector.nombre} />)}
              </Tabs>
              {mapaSectores.map((sector, index) => {
                if (index !== sectorMapaSeleccionado) return null;

                const limites = limitesMapaSectores.get(sector.idSector);
                if (!limites) return null;

                return (
                  <Box key={sector.idSector} sx={{ overflowX: "auto", p: 1.5, bgcolor: "grey.50" }}>
                    <Box sx={{ position: "relative", width: "100%", maxWidth: `${limites.ancho * 0.76}px`, aspectRatio: `${limites.ancho} / ${limites.alto}`, bgcolor: "background.paper", border: 1, borderColor: "divider" }}>
                      {sector.mesas.map((mesa) => {
                        const ocupada = mesasAbiertas.some((item) => item.idMesa === mesa.numero);
                        const seleccionada = numeroMesa === String(mesa.numero) && nombreComanda === "";
                        return (
                          <Tooltip key={mesa.idMesaMapa} title={`${ocupada ? "Ocupada" : "Libre"}. Toque para seleccionar.`}>
                            <Button
                              variant={seleccionada ? "contained" : "outlined"}
                              color={ocupada ? "error" : "success"}
                              onClick={() => seleccionarMesaMapa(mesa)}
                              sx={{
                                position: "absolute",
                                left: `${((mesa.x - limites.minX) / limites.ancho) * 100}%`,
                                top: `${((mesa.y - limites.minY) / limites.alto) * 100}%`,
                                width: `${(mesa.ancho / limites.ancho) * 100}%`,
                                height: `${(mesa.alto / limites.alto) * 100}%`,
                                minWidth: 42,
                                minHeight: 32,
                                p: 0.5,
                                lineHeight: 1.1,
                                textTransform: "none",
                                overflow: "hidden",
                                borderRadius: mesa.forma === "Redonda" ? "50%" : 1,
                                bgcolor: seleccionada ? undefined : (ocupada ? "error.light" : "success.light"),
                                color: seleccionada ? undefined : (ocupada ? "error.contrastText" : "success.contrastText"),
                                "&:hover": { bgcolor: ocupada ? "error.main" : "success.main" }
                              }}
                            >
                              <Box component="span">
                                <strong>Mesa {mesa.numero}</strong>
                                {mesa.nombre && <Box component="span" sx={{ display: "block", fontSize: "0.72rem" }}>{mesa.nombre}</Box>}
                              </Box>
                            </Button>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
          </Collapse>
        </Box>
      )}

      {localesComanda?.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No hay locales habilitados para Comanda Mesa. Configure los locales desde Parámetros Globales.
        </Alert>
      )}

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={localesComanda?.length === 1 ? 6 : 4}>
          <TextField
            label="Nombre (opcional)"
            variant="outlined"
            fullWidth
            value={nombreComanda}
            inputProps={{ maxLength: 120 }}
            onChange={(e) => handleNombreComandaChange(e.target.value)}
            helperText="Al escribir un nombre se crea una mesa nueva. Toque una abierta para modificarla."
          />
        </Grid>
      </Grid>

      {mesaAbiertaDetalle && !mesaAbiertaCargada && (idMesaSeleccionada == null || idMesaSeleccionada > 0) && (
        <Alert
          severity="warning"
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <AlertTitle>Mesa abierta</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            La mesa {numeroMesa} ya está abierta. Puede agregar una nueva comanda o cargar el detalle existente.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button color="primary" size="medium" variant="contained" onClick={traerDetalleMesaAbierta} sx={{ minHeight: 42, px: 2, fontWeight: 700 }}>
              CARGAR DETALLE
            </Button>
            <Button color="secondary" size="medium" variant="outlined" onClick={limpiarSeleccion} sx={{ minHeight: 42, px: 2, fontWeight: 700 }}>
              LIMPIAR
            </Button>
          </Box>
        </Alert>
      )}

      {mesaAbiertaCargada && (
        <Alert
          severity="success"
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            {idMesaSeleccionada != null && idMesaSeleccionada < 0
              ? `Modificando la comanda ${nombreComanda}. Al guardar se actualiza esta comanda; escribir el mismo nombre en el campo crea otra mesa.`
              : `Detalle existente de la mesa ${numeroMesa} cargado. Puede agregar más productos.`}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button color="secondary" size="medium" variant="outlined" onClick={limpiarSeleccion} sx={{ minHeight: 42, px: 2, fontWeight: 700 }}>
              LIMPIAR
            </Button>
          </Box>
        </Alert>
      )}

      {mesaAbiertaDetalle && (
        <Box sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2 }, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {mostrarDesgloseTotalMesa ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Total anterior</Typography>
                <Typography variant="body2" fontWeight={600}>{CurrencyFormatter.format(totalAnteriorMesa)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Productos agregados</Typography>
                <Typography variant="body2" fontWeight={600}>{CurrencyFormatter.format(totalProductosAgregados)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>Nuevo total</Typography>
                <Typography variant="h6" fontWeight={700} color="primary" sx={{ textAlign: "right" }}>
                  {CurrencyFormatter.format(calcularTotalLineas(lineaVenta))}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12} sm="auto">
                <Typography variant="subtitle1" fontWeight="bold">
                  Total {numeroMesa ? `mesa ${numeroMesa}` : nombreComanda}
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  {CurrencyFormatter.format(calcularTotalMesa())}
                </Typography>
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {/* Add Product Button */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Button
          onClick={handleOpenProductDialog}
          variant="contained"
          color="primary"
          fullWidth
          disabled={loadingProductos || checkingMesa || !idLocal || (!numeroMesa.trim() && !nombreComanda.trim())}
          sx={{ 
            textTransform: "none", 
            fontWeight: "bold", 
            py: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.9rem', sm: '1rem' }
          }}
          startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <AddIcon />}
        >
          {loadingProductos ? "Cargando Menu..." : "Agregar Productos"}
        </Button>
      </Box>

      {/* Product Selection Dialog */}
      <Dialog
        open={selectDialogOpen} 
        fullWidth 
        maxWidth="md"
        fullScreen={fullScreenProductDialog}
        onClose={closeProductDialog}
      >
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            height: fullScreenProductDialog ? "100%" : undefined,
            minHeight: fullScreenProductDialog ? 0 : { xs: "100%", sm: 560 },
            overflow: fullScreenProductDialog ? "hidden" : undefined
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="h5" sx={{ fontSize: { xs: "1.25rem", sm: "1.35rem" }, fontWeight: 700 }}>
              Agregar productos
            </Typography>
            <Chip color="primary" label={`${lineaVenta.reduce((total, linea) => total + linea.cantidad, 0)} en la comanda`} />
          </Box>
          <TextField
            fullWidth
            value={busquedaProducto}
            onChange={(event) => setBusquedaProducto(event.target.value)}
            placeholder="Buscar por nombre o código"
            size="small"
            sx={{ mb: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Rubro
            </Typography>
            <Box
              role="group"
              aria-label="Filtrar por rubro"
              sx={{ display: "flex", gap: 1, overflowX: "auto", py: 1, WebkitOverflowScrolling: "touch" }}
            >
              <Chip
                label="Todos"
                color={!rubroSeleccionado ? "primary" : "default"}
                variant={!rubroSeleccionado ? "filled" : "outlined"}
                onClick={() => {
                  setRubroSeleccionado("");
                  setSubrubroSeleccionado("");
                }}
                sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }}
              />
              {rubrosDisponibles.map((rubro) => {
                const seleccionado = rubroSeleccionado === rubro.id;
                return (
                  <Chip
                    key={rubro.id}
                    label={rubro.nombre}
                    color={seleccionado ? "primary" : "default"}
                    variant={seleccionado ? "filled" : "outlined"}
                    onClick={() => {
                      setRubroSeleccionado(seleccionado ? "" : rubro.id);
                      setSubrubroSeleccionado("");
                    }}
                    sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }}
                  />
                );
              })}
            </Box>
            {subrubrosDisponibles.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Subrubro
                </Typography>
                <Box
                  role="group"
                  aria-label="Filtrar por subrubro"
                  sx={{ display: "flex", gap: 1, overflowX: "auto", py: 1, WebkitOverflowScrolling: "touch" }}
                >
                  <Chip
                    label="Todos"
                    color={!subrubroSeleccionado ? "secondary" : "default"}
                    variant={!subrubroSeleccionado ? "filled" : "outlined"}
                    onClick={() => setSubrubroSeleccionado("")}
                    sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }}
                  />
                  {subrubrosDisponibles.map((subrubro) => {
                    const seleccionado = subrubroSeleccionado === subrubro.id;
                    return (
                      <Chip
                        key={subrubro.id}
                        label={subrubro.nombre}
                        color={seleccionado ? "secondary" : "default"}
                        variant={seleccionado ? "filled" : "outlined"}
                        onClick={() => setSubrubroSeleccionado(seleccionado ? "" : subrubro.id)}
                        sx={{ minHeight: 40, flexShrink: 0, fontWeight: 600 }}
                      />
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            Podés combinar la búsqueda con rubro y subrubro. Tocá una tarjeta o Agregar para sumar el producto.
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: { sm: 0.5 } }}>
            <Grid container spacing={1.5}>
              {productosFiltrados.map((producto) => {
                const cantidad = cantidadProductoAgregada(producto);
                return (
                  <Grid item xs={12} sm={6} md={4} key={producto.IDPRODUCTO || producto.CODIGO}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "border-color 160ms ease, box-shadow 160ms ease",
                        "&:hover": { borderColor: "primary.main", boxShadow: 3 }
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleProductClick(producto)}
                        aria-label={`Agregar ${producto.PRODUCTO} a la comanda`}
                        sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "stretch" }}
                      >
                        <ImagenProductoComanda producto={producto} user={userLoggedIn} />
                        <CardContent sx={{ flex: 1, width: "100%" }}>
                          <Typography variant="caption" color="text.secondary">{producto.CODIGO}</Typography>
                          <Typography fontWeight={700}>{producto.PRODUCTO}</Typography>
                          {(producto.RUBRO || producto.SUBRUBRO) && (
                            <Typography variant="caption" color="text.secondary">
                              {[producto.RUBRO, producto.SUBRUBRO].filter(Boolean).join(" · ")}
                            </Typography>
                          )}
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>{CurrencyFormatter.format(producto.CONTADO)}</Typography>
                        </CardContent>
                      </CardActionArea>
                      <CardActions sx={{ px: 1.5, pb: 1.5 }}>
                        <Badge badgeContent={cantidad} color="secondary" sx={{ width: "100%" }}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddShoppingCartIcon />}
                            onClick={() => handleProductClick(producto)}
                            sx={{ minHeight: 44, fontWeight: 700 }}
                          >
                            Agregar
                          </Button>
                        </Badge>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            {productosFiltrados.length === 0 && (
              <Alert severity="info">No se encontraron productos para la búsqueda y los filtros seleccionados.</Alert>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button variant="contained" color="success" onClick={closeProductDialog}>Listo, volver a la comanda</Button>
          </Box>
        </Box>
      </Dialog>

      {/* Order Summary */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {numeroMesa ? `Mesa: ${numeroMesa}` : `Nombre: ${nombreComanda || "Sin asignar"}`}
        </Typography>
        <Divider sx={{ mb: { xs: 1, sm: 2 } }} />

        {lineaVenta.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            No hay productos en la comanda. Agregue productos para comenzar.
          </Alert>
        ) : (
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {lineaVenta.map((linea, index) => (
              <Grid item xs={12} key={index}>
                <Card elevation={2}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                    <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
                      {/* Product Name and Code - Full width on mobile */}
                      <Grid item xs={12} sm={5}>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                          {linea.producto.PRODUCTO}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Codigo: {linea.producto.CODIGO}
                        </Typography>
                        {linea.notas && !expandedNotes[index] && (
                          <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {linea.notas}
                          </Typography>
                        )}
                      </Grid>
                      
                      {/* Quantity Controls - Left aligned on mobile */}
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, justifyContent: { xs: 'flex-start', sm: 'center' } }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleDecrementQuantity(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small"
                            type="text"
                            inputMode="decimal"
                            value={editingQuantities[index] ?? formatearCantidad(linea.cantidad)}
                            onFocus={() => beginQuantityEdit(index)}
                            onChange={(event) => changeQuantityText(index, event.target.value)}
                            onBlur={() => finishQuantityEdit(index)}
                            inputProps={{ "aria-label": `Cantidad de ${linea.producto.PRODUCTO}`, step: pasoCantidad, min: pasoCantidad }}
                            sx={{ width: { xs: 82, sm: 100 } }}
                          />
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleIncrementQuantity(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                      
                      {/* Price - Right aligned */}
                      <Grid item xs={6} sm={3}>
                        <Typography 
                          variant="h6" 
                          align="right"
                          sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                        >
                          {CurrencyFormatter.format(getUnitario(linea) * linea.cantidad)}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          align="right"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                        >
                          {CurrencyFormatter.format(getUnitario(linea))} c/u
                        </Typography>
                      </Grid>
                      
                      {/* Action Buttons - Horizontal on mobile, vertical on desktop */}
                      <Grid item xs={12} sm={1}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: { xs: 'row', sm: 'column' }, 
                          gap: 0.5,
                          justifyContent: { xs: 'flex-end', sm: 'center' }
                        }}>
                          <IconButton 
                            size="small"
                            color="primary"
                            onClick={() => toggleNotesExpand(index)}
                            title={linea.notas ? "Editar notas" : "Agregar notas"}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            {linea.notas ? <EditNoteIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                          </IconButton>
                          <IconButton 
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRow(index)}
                            sx={{ p: { xs: 0.5, sm: 1 } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {/* Notes Section - Collapsible */}
                    <Collapse in={expandedNotes[index]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
                        <TextField
                          label="Notas / Comentarios"
                          placeholder="Ej: Sin cebolla, bien cocido, para llevar..."
                          multiline
                          rows={2}
                          fullWidth
                          value={editingNotes[index] || ""}
                          onChange={(e) => handleNotesChange(index, e.target.value)}
                          variant="outlined"
                          size={window.innerWidth < 600 ? "small" : "medium"}
                        />
                        <Box sx={{ 
                          mt: 1, 
                          display: 'flex', 
                          justifyContent: 'flex-end', 
                          gap: { xs: 0.5, sm: 1 },
                          flexWrap: 'wrap'
                        }}>
                          <Button
                            size="small"
                            onClick={() => toggleNotesExpand(index)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => saveNotes(index)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Guardar
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Total and Submit */}
      {lineaVenta.length > 0 && (
        <Card 
          elevation={3} 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            position: { xs: 'sticky', sm: 'static' },
            bottom: { xs: 0, sm: 'auto' },
            zIndex: { xs: 1000, sm: 'auto' }
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container alignItems="center" justifyContent="space-between" spacing={{ xs: 1, sm: 0 }}>
              <Grid item xs={12} sm="auto">
                <Typography 
                  variant="h5"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Total
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Typography 
                  variant="h4" 
                  fontWeight="bold"
                  sx={{ 
                    fontSize: { xs: '1.75rem', sm: '2.125rem' },
                    textAlign: { xs: 'left', sm: 'right' }
                  }}
                >
                  {CurrencyFormatter.format(calcularTotal())}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions sx={{ 
            justifyContent: { xs: 'stretch', sm: 'flex-end' }, 
            p: { xs: 1.5, sm: 2 },
            pt: { xs: 0, sm: 2 }
          }}>
            <Button
              onClick={guardarComanda}
              disabled={enviandoComanda}
              variant="contained"
              color="success"
              size="large"
              fullWidth={window.innerWidth < 600}
              sx={{ 
                textTransform: "none", 
                fontWeight: "bold", 
                px: { xs: 2, sm: 4 },
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
              startIcon={enviandoComanda ? <CircularProgress color="inherit" size={18} /> : undefined}
            >
              {enviandoComanda ? "Enviando..." : (mesaAbiertaDetalle && !mesaAbiertaCargada ? "Enviar nueva comanda" : (idMesaSeleccionada != null && idMesaSeleccionada < 0 ? "Guardar cambios" : "Enviar Comanda"))}
            </Button>
          </CardActions>
        </Card>
      )}

      {idLocal && (
        <Box sx={{ mt: { xs: 3, sm: 4 }, p: { xs: 1.5, sm: 2 }, border: 1, borderColor: "divider", borderRadius: 2, bgcolor: "grey.50" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 2, p: 1.5, flexWrap: "wrap", bgcolor: "rgba(248, 154, 49, 0.14)", borderRadius: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TableBarIcon color="primary" />
              <Typography variant="h6" fontWeight={800}>
                Mesas abiertas
              </Typography>
              {!loadingMesasAbiertas && mesasAbiertas.length > 0 && (
                <Chip label={mesasAbiertas.length} size="small" variant="outlined" />
              )}
            </Box>
            {haySeleccionAbierta && (
              <Button
                size="small"
                variant="text"
                startIcon={<ClearIcon />}
                onClick={limpiarSeleccion}
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                Limpiar
              </Button>
            )}
          </Box>
          {loadingMesasAbiertas ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.5 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Cargando abiertas...</Typography>
            </Box>
          ) : mesasAbiertas.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No hay mesas ni comandas abiertas en este local.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {mesasAbiertas.map((mesa) => {
                const seleccionada = idMesaSeleccionada === mesa.idMesa
                  || (mesa.idMesa > 0 && numeroMesa === mesa.idMesa.toString() && idMesaSeleccionada == null);
                const hora = mesa.fecha
                  ? new Date(mesa.fecha).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                  : "";
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={mesa.idMesa}>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        bgcolor: seleccionada ? "action.selected" : "action.hover",
                        border: 1,
                        borderColor: seleccionada ? "primary.main" : "transparent",
                        borderRadius: 2
                      }}
                    >
                      <CardActionArea
                        onClick={() => seleccionarMesaAbierta(mesa)}
                        sx={{ height: "100%", p: 1.25, textAlign: "left", borderRadius: 2 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {mesa.idMesa > 0 ? `Mesa ${mesa.idMesa}` : mesa.nombre || "Sin nombre"}
                          </Typography>
                          {hora && <Typography variant="caption" color="text.secondary">{hora}</Typography>}
                        </Box>
                        {mesa.idMesa > 0 && mesa.nombre && (
                          <Typography variant="body2" color="primary.main" noWrap>
                            {mesa.nombre}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {mesa.idMesa > 0
                            ? `${mesa.cantidadComandas} ${mesa.cantidadComandas === 1 ? "comanda" : "comandas"} · ${CurrencyFormatter.format(mesa.total)}`
                            : CurrencyFormatter.format(mesa.total)}
                        </Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

    </Box>
  );
};

export default ComandaMesa;



